import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import '../styles/Events.css';

/* ════════════════════════════════════════════════════════════
   EVENTS — الفعاليات
   Past events archive + upcoming events
   Managed via admin panel · Stored in Supabase
   ════════════════════════════════════════════════════════════ */

const EVENT_TYPES = {
  'أمسية شعرية': '🎤',
  'مؤتمر أدبي': '🏛️',
  'مهرجان دولي': '🌍',
  'ندوة': '📚',
  'توقيع كتاب': '📖',
  'لقاء تلفزيوني': '📺',
};

const FALLBACK_EVENTS = [
  { id: 'f1', title: 'مؤتمر الشعر العربي', description: 'مشاركة في مؤتمر الشعر العربي', event_date: '2019-03-15', city: 'أبو ظبي', country: 'الإمارات', event_type: 'مؤتمر أدبي', is_upcoming: false },
  { id: 'f2', title: 'مهرجان الشعر الآسيوي الدولي', description: 'تمثيل لبنان في المهرجان', event_date: '2019-09-20', city: 'نور سلطان', country: 'كازاخستان', event_type: 'مهرجان دولي', is_upcoming: false },
  { id: 'f3', title: 'أمسية شعرية في بعقلين', description: 'أمسية شعرية في بلدة بعقلين — الشوف', event_date: '2023-05-10', city: 'بعقلين', country: 'لبنان', event_type: 'أمسية شعرية', is_upcoming: false },
  { id: 'f4', title: 'فعاليات أدبية في الأردن', description: 'قصيدة شكر وامتنان ودوحة الشرق', event_date: '2024-04-20', city: 'عمّان', country: 'الأردن', event_type: 'أمسية شعرية', is_upcoming: false },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const months = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function formatYear(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).getFullYear().toString();
}

export default function Events() {
  const [events, setEvents] = useState(FALLBACK_EVENTS);
  const [filter, setFilter] = useState('all'); // 'all', 'upcoming', 'past'
  const [revealed, setRevealed] = useState(new Set());
  const cardRefs = useRef({});

  /* ── Fetch from Supabase ──────────────────────── */
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .order('event_date', { ascending: false });

        if (error) throw error;
        if (data && data.length > 0) setEvents(data);
      } catch (_) {}
    };

    fetchEvents();

    const channel = supabase
      .channel('events_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, () => {
        fetchEvents();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  /* ── Scroll reveal ─────────────────────────────── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) setRevealed(p => new Set([...p, e.target.dataset.eid]));
      }),
      { threshold: 0.1 }
    );
    const timer = setTimeout(() => {
      Object.values(cardRefs.current).forEach(el => el && obs.observe(el));
    }, 100);
    return () => { clearTimeout(timer); obs.disconnect(); };
  }, [events, filter]);

  /* ── Filter — auto-detect upcoming vs past by date ─── */
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = events.filter(e => {
    if (!e.event_date) return e.is_upcoming; // fallback to manual flag if no date
    return new Date(e.event_date) >= today;
  }).sort((a, b) => new Date(a.event_date) - new Date(b.event_date)); // soonest first

  const past = events.filter(e => {
    if (!e.event_date) return !e.is_upcoming;
    return new Date(e.event_date) < today;
  }).sort((a, b) => new Date(b.event_date) - new Date(a.event_date)); // most recent first

  const filtered = filter === 'upcoming' ? upcoming : filter === 'past' ? past : [...upcoming, ...past];

  return (
    <section id="events" className="ev-section">
      {/* Ambient */}
      <div className="ev-ambient" aria-hidden="true">
        <b>ف</b><b>ع</b><b>ل</b>
      </div>

      <div className="ev-container">

        {/* Header */}
        <header className="ev-header">
          <span className="ev-eyebrow">أمسيات ومشاركات</span>
          <h2 className="ev-title">الفعاليات</h2>
          <div className="ev-rule"><span /><em>◆</em><span /></div>
          <p className="ev-sub">
            مشاركات الشاعر في الأمسيات والمهرجانات والمؤتمرات الأدبية حول العالم
          </p>
        </header>

        {/* Filter tabs */}
        <div className="ev-filters">
          <button className={`ev-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            الكل <em>{events.length}</em>
          </button>
          {upcoming.length > 0 && (
            <button className={`ev-filter-btn ev-filter-upcoming ${filter === 'upcoming' ? 'active' : ''}`} onClick={() => setFilter('upcoming')}>
              قادمة <em>{upcoming.length}</em>
            </button>
          )}
          <button className={`ev-filter-btn ${filter === 'past' ? 'active' : ''}`} onClick={() => setFilter('past')}>
            سابقة <em>{past.length}</em>
          </button>
        </div>

        {/* Upcoming events — special highlight */}
        {filter !== 'past' && upcoming.length > 0 && (
          <div className="ev-upcoming-section">
            <h3 className="ev-section-label">✨ فعاليات قادمة</h3>
            <div className="ev-upcoming-grid">
              {upcoming.map((event, idx) => (
                <div
                  key={event.id}
                  ref={el => { cardRefs.current[event.id] = el; }}
                  data-eid={event.id}
                  className={`ev-upcoming-card ${revealed.has(event.id) ? 'ev-card--in' : ''}`}
                  style={{ '--delay': `${idx * 100}ms` }}
                >
                  <div className="ev-upcoming-pulse" />
                  <div className="ev-upcoming-date">
                    <span className="ev-upcoming-day">{event.event_date ? new Date(event.event_date).getDate() : '—'}</span>
                    <span className="ev-upcoming-month">{event.event_date ? ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'][new Date(event.event_date).getMonth()] : ''}</span>
                    <span className="ev-upcoming-year">{formatYear(event.event_date)}</span>
                  </div>
                  <div className="ev-upcoming-info">
                    <span className="ev-upcoming-type">
                      {EVENT_TYPES[event.event_type] || '📌'} {event.event_type}
                    </span>
                    <h4 className="ev-upcoming-title">{event.title}</h4>
                    <p className="ev-upcoming-desc">{event.description}</p>
                    <div className="ev-upcoming-location">
                      📍 {event.location ? `${event.location} — ` : ''}{event.city}، {event.country}
                    </div>
                  </div>
                  {event.image_url && (
                    <img src={event.image_url} alt={event.title} className="ev-upcoming-img" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past events — timeline style */}
        {filter !== 'upcoming' && past.length > 0 && (
          <div className="ev-past-section">
            {filter === 'all' && upcoming.length > 0 && (
              <h3 className="ev-section-label">📜 أرشيف الفعاليات</h3>
            )}
            <div className="ev-timeline">
              <div className="ev-timeline-line" />
              {past.map((event, idx) => (
                <div
                  key={event.id}
                  ref={el => { cardRefs.current[event.id] = el; }}
                  data-eid={event.id}
                  className={`ev-timeline-item ${idx % 2 === 0 ? 'ev-timeline-right' : 'ev-timeline-left'} ${revealed.has(event.id) ? 'ev-card--in' : ''}`}
                  style={{ '--delay': `${idx * 120}ms` }}
                >
                  <div className="ev-timeline-dot">
                    <span>{EVENT_TYPES[event.event_type] || '📌'}</span>
                  </div>
                  <div className="ev-timeline-card">
                    {event.image_url && (
                      <div className="ev-card-img-wrap">
                        <img src={event.image_url} alt={event.title} className="ev-card-img" />
                      </div>
                    )}
                    <div className="ev-card-body">
                      <div className="ev-card-meta">
                        <span className="ev-card-date">{formatDate(event.event_date)}</span>
                        <span className="ev-card-type">{event.event_type}</span>
                      </div>
                      <h4 className="ev-card-title">{event.title}</h4>
                      <p className="ev-card-desc">{event.description}</p>
                      <div className="ev-card-location">
                        📍 {event.city}، {event.country}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="ev-empty">
            <span>📅</span>
            <p>لا توجد فعاليات في هذا التصنيف</p>
          </div>
        )}

        {/* Bottom ornament */}
        <div className="ev-ornament">
          <span /><em>✦ ✦ ✦</em><span />
        </div>
      </div>
    </section>
  );
}